import AlternativeFeature from "@components/alternatives/AlternativeFeature";
import { AlternativeInformation } from "@components/alternatives/AlternativeInformation";
import { AlternativesCards } from "@components/alternatives/AlternativesCards";
import { AlternativesCTASection } from "@components/alternatives/AlternativesCTASection";
import { AlternativesTopBannerCTASection } from "@components/alternatives/AlternativesTopBannerCTASection";
import { ContentParagraph } from "@components/alternatives/ContentParagraph";
import { Herosection, HeroSectionProps } from "@components/alternatives/HeroSection";
import { OverviewContent, OverviewGrid } from "@components/alternatives/OverviewGrid";
import AwardsSection from "@components/new-ui/AwardsSection";
import CTASection from "@components/new-ui/CTASection";
import NewLayout from "@components/new-ui/Layout";
import { ClientFeedback, Navbar } from "@pages/case-study/alrannan-administrative-services";
import Image from "next/image";
import React from "react";
import { alternativesCardsDetails, doubleTickNavLinks } from "../../components/alternatives/constant";
import { AlternativesInfo } from "./interakt";
import { extractTextFromJSX } from "@utils/schemaGenerator";

const herosectionData: HeroSectionProps = {
    chipTitle: "Alternatives",
    title: "Top 10 DoubleTick Alternatives for WhatsApp Automation in 2025",
    primaryButtonText: "Try for Free",
    imageUrl: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/doubletick-hero.png",
    imgAlt: "doubletick",
};

const gallabox: AlternativesInfo[] = [
    {
        id: "1",
        title: "1. Gallabox",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">Best for end-to-end customer lifecycle management on WhatsApp</p>
            </div>
        ),
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/create-segment.png",
        imgAlt: "create-segment",
        list: [
            { item: "Creating audience segments in Gallabox for personalized WhatsApp marketing campaigns" },
            {
                item: (
                    <>
                        Gallabox is an AI-driven{" "}
                        <a href="https://gallabox.com/whatsapp-business-api" className="underline px-1">
                            WhatsApp Business API platform
                        </a>
                        designed to engage buyers at every stage — from lead nurturing and qualification to conversion and long-term retention.
                    </>
                ),
            },
        ],
        features: {
            featuresList: [
                {
                    item: "Nurturing: Use drip campaigns to send personalized, behavior-based messages to keep customers engaged.",
                },
                {
                    item: "Qualification: AI chatbots qualify leads by gathering info and filtering out non-eligible prospects.",
                },
                {
                    item: "Conversion: Create product catalogs within WhatsApp for easy browsing and direct purchases.",
                },
                {
                    item: "Retention: Segment audiences to send personalized offers and updates, driving repeat purchases.",
                },
            ],
        },
    },
];

const quickReply: AlternativesInfo[] = [
    {
        id: "2",
        title: "2. QuickReply",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/quickreply.png",
        imgAlt: "quickreply",
        description: "Best WhatsApp marketing solution for Shopify stores",
        list: [
            {
                item: "If you run an ecommerce store — specifically on Shopify — then QuickReply can be a great DoubleTick alternative for you. Purpose-built for ecommerce stores, QuickReply allows you to set up workflows for cart recovery, order updates, and upsells.",
            },
            {
                item: "Besides, your Instagram DMs and Facebook Messenger conversations will also be integrated with QuickReply — so you can manage all conversations from a single, central inbox. Not just WhatsApp conversations.",
            },
        ],
    },
];

const wati: AlternativesInfo[] = [
    {
        id: "3",
        title: "3. Wati",
        description: "Best for multi-step custom WhatsApp chatbots",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/wati.png",
        imgAlt: "wati",
        list: [
            {
                item: "Wati is a WhatsApp Business API solution that’s great for non-technical users. It comes with a no-code chatbot builder that allows you to create complex chatbot flows with up to 200 consecutive steps. It’s also relatively easy to set up when compared to DoubleTick. ",
            },
            {
                item: "Another advantage of Wati is its Team Inbox, which centralizes not just WhatsApp chats but also Instagram DMs and Facebook Messenger conversations in one place.",
            },
        ],
    },
];

const brevo: AlternativesInfo[] = [
    {
        id: "4",
        title: "4. Brevo",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/brevo.png",
        imgAlt: "brevo",
        description: "Best for SMBs requiring omnichannel marketing and sales automation (WhatsApp included)",
        list: [
            {
                item: "Brevo (formerly Sendinblue) is primarily an email marketing platform that also includes WhatsApp and SMS marketing. However, like many all-in-one marketing tools that recently added WhatsApp support, it offers only the basics — think drip campaigns, simple broadcasts, and basic automation.",
            },
            {
                item: "If you're already using Brevo for email marketing and want a lightweight WhatsApp add-on, it might be a convenient option. Especially as you get to leverage Brevo’s customer data platform to score your leads and create custom audience engagement reports.",
            },
        ],
    },
];

const interakt: AlternativesInfo[] = [
    {
        id: "5",
        title: "5. Interakt",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/interakt.png",
        imgAlt: "interakt",
        description: "Best WhatsApp marketing plus Instagram automation tool",
        list: [
            {
                item: "If you’re a small business focusing on both WhatsApp and Instagram marketing and want a no-hassle automation tool, then Interakt might be a good option for you. It’s one of the few WhatsApp Business API solutions that also supports Instagram automations — like automated custom replies to DMs and in comments. ",
            },
            { item: "And unlike DoubleTick, Interakt integrates with multiple CRMs, ERPs, payment gateways, and accounting apps — so your tech stack is well-connected." },
        ],
    },
];

const superlemon: AlternativesInfo[] = [
    {
        id: "6",
        title: "6. Superlemon",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/superlemon.png",
        imgAlt: "superlemon",
        description: "Best affordable WhatsApp Business API platform for Shopify and Wix stores",
        list: [
            {
                item: "SuperLemon is a lightweight WhatsApp Marketing and Support plugin for Shopify and Wix stores. Unlike the more sophisticated tools on this list, SuperLemon is more straightforward and beginner-friendly, making it a great choice for small businesses that need quick, no-fuss automation without a steep learning curve.",
            },
        ],
    },
];

const zixflow: AlternativesInfo[] = [
    {
        id: "7",
        title: "7. Zixflow",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/zix-flow.png",
        imgAlt: "zix-flow",
        description: "Best for omnichannel lead generation and outreach",
        list: [
            {
                item: "Zixflow is an all-in-one, AI-powered lead engagement platform that enables businesses to run automated campaigns across email, SMS, and WhatsApp. As such, it gives you a no-frills CRM, team inbox, and email management features to maximize lead outreach on all three channels.",
            },
            {
                item: "It’s a DoubleTick alternative worth checking out for businesses that depend on ABM and cold outreach for lead generation, as it also allows you to find and validate prospect emails for better deliverability. ",
            },
        ],
    },
];

const aiSensy: AlternativesInfo[] = [
    {
        id: "8",
        title: "8. AiSensy",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/aisensy.png",
        imgAlt: "aisensy",
        description: "Best for one-click WhatsApp retargeting campaigns",
        list: [
            {
                item: "AiSensy is positioned as a “WhatsApp Engagement Platform” that enables businesses to automate customer interactions, send bulk messages, and manage WhatsApp marketing campaigns. It also has a solid set of integrations (1000+) and allows you to build custom ones using connectors like Zapier or Pabbly. ",
            },
            {
                item: "However, if you're a large team or handle high-volume conversations, you may encounter additional — sometimes hidden — charges for features like the chatbot builder, virtual numbers, and live agent dashboards, despite AiSensy’s claim of unlimited users on all plans.",
            },
        ],
    },
];

const respondIo: AlternativesInfo[] = [
    {
        id: "9",
        title: "9. Respond.io",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/respond-io.png",
        imgAlt: "respond-io",
        description: "Best omnichannel marketing automation platform",
        list: [
            {
                item: "Respond.io is a customer conversation management platform that allows you to manage messages (and leads) from various channels — such as WhatsApp, Facebook Messenger, Instagram, TikTok, email, and more — in a single place. It’s a customer engagement-focused platform and helps you handle the entire journey — capturing, converting, and retaining customers.",
            },
            { item: "You can also train their AI Agent with your brand information and help documents, and customize its tone to ensure an on-brand customer experience." },
        ],
    },
];

const zoko: AlternativesInfo[] = [
    {
        id: "10",
        title: "10. Zoko",
        image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/zoko.png",
        imgAlt: "zoko",
        description: "Best for WhatsApp + Shopify flows",
        list: [
            {
                item: "Zoko is another great DoubleTick alternative for Shopify stores. Apart from the regular WhatsApp automations like click-to-WhatsApp ads and broadcasts, it also comes with 19 prebuilt WhatsApp flow templates for common ecommerce use cases like reconfirming COD orders, abandoned cart recovery, and shipping updates.",
            },
            { item: "And if you have a specific ask, you can integrate with ChatGPT to build your own custom AI bot." },
        ],
    },
];

const content: OverviewContent = {
    title: ["Platform", "Best for", "Standout feature", "Pricing"],
    rows: [
        [["Gallabox"], ["Businesses of all sizes, across industries"], ["AI-powered, multilingual bots"], ["Starts at $89 per year (3 users); free trial available"]],
        [["QuickReply"], ["Ecommerce businesses"], ["Broadcast retry"], ["Starts at $39 per month; free trial available"]],
        [["Wati"], ["Small and mid-sized businesses"], ["Multi-step bots"], ["Starts at $39 per month; free trial available"]],

        [["Brevo"], ["SMBs requiring email + WhatsApp marketing"], ["Intelligent lead scoring"], ["Free plan available"]],
        [["Interakt"], ["SMBs requiring Instagram + WhatsApp automation"], ["Custom replies to Instagram DMs"], ["Starts at $17 per month; free trial available"]],
        [["SuperLemon"], ["Small Shopify and Wix stores"], ["Tags to categorize chats"], ["Free plan available"]],
        [["Zixflow"], ["SMBs requiring omnichannel lead outreach"], ["Email validation"], ["Free plan available"]],
        [["AiSensy"], ["SMBs that send many campaigns and use fewer chatbots."], ["WhatsApp retargeting"], ["Free plan available"]],
        [["Respond.io"], ["Enterprise omnichannel marketing automation"], ["Automated lead qualification"], ["Starts at $79 per month; free trial available"]],
        [["Zoko"], ["Small and mid-sized businesses"], ["Pop-up forms"], ["PStarts at $39.99 per month; free trial available"]],
    ],
};

const allAlternatives: AlternativesInfo[] = [...gallabox, ...quickReply, ...wati, ...brevo, ...interakt, ...superlemon, ...zixflow, ...wati, ...aiSensy, ...respondIo, ...zoko];

const simplifiedAlternatives: { title: string; description: string; imageUrl: string }[] = allAlternatives.map(({ title, description, image }) => ({
    title,
    description: extractTextFromJSX(description),
    imageUrl: image as string,
}));

const DoubleTickIo: React.FC = () => {
    return (
        <NewLayout
            title=" 10 Best DoubleTick Alternatives for 2025 | Gallabox"
            description=" Looking for Doubletick.io alternatives? Compare top competitors and choose the right replacement to streamline your WhatsApp sales."
            canonical="https://gallabox.com/alternatives/doubletick"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/doubletick-og.png" }}
            schema={{
                heroOrOgImageAlt: "alternative",
                heroOrOgImageDescription: "Top 10 DoubleTick Alternatives for WhatsApp Automation in 2025",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/alternatives/doubletick-io/doubletick-hero.png",
                alternativesInfo: simplifiedAlternatives,
            }}
        >
            <Herosection {...herosectionData} />

            <section className="max-w-6xl md:mx-36 mx-auto md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <p className="text-gray-900 text-xl font-semibold pb-5">Table of contents</p>
                        <Navbar links={doubleTickNavLinks} />
                    </div>
                </div>

                <div className="col-span-4 md:pl-4 px-4">
                    <ContentParagraph
                        content={[
                            "With WhatsApp being a popular messaging app in over 180 countries, it’s no wonder businesses globally are prioritizing WhatsApp marketing.  As of 2024, over 50 million businesses (Source: Meetanshi) use WhatsApp to connect with customers.",
                        ]}
                    />
                    <ContentParagraph
                        content={
                            <p className="text-lg pb-6 text-left">
                                But success on WhatsApp isn’t just about being present, it depends on choosing the right
                                <a href="https://business.whatsapp.com/blog/use-whatsapp-business-goals" className="underline text-blue-500 px-1">
                                    WhatsApp Business API platform.
                                </a>
                                The right tool automates workflows, personalizes interactions at scale, and expands your reach. And the wrong one doesn’t just slow you down — it
                                can frustrate customers and hurt engagement.
                            </p>
                        }
                    />
                    <ContentParagraph
                        content={[
                            "So let’s talk about one of the more popular WhatsApp automation tools in the market — DoubleTick. Unlike other similar tools on the market, DoubleTick emphasizes sales-centric features — like automated ordering bots and direct orders via AI image recognition bots — over high-volume marketing campaigns, A/B testing, and such.",
                            "And for small businesses — with basic audience segmentation and contact management requirements — it can also double as a sales CRM.",
                            "However, despite being a tool for small businesses, it’s one of the more expensive tools and lacks quite a few important features like dead audience filtering and nuanced automations.",
                            "In this article, we explore where DoubleTick falls behind when compared to other WhatsApp automation tools and in which instances you might do better with one of its competitors — whether that’s a certain feature, more support, or budget.",
                        ]}
                    />
                    <h2 className="text-black text-5xl font-semibold pb-8 mt-10">The 10 Best DoubleTick Alternatives and Competitors in 2025</h2>
                    <ContentParagraph
                        content={[
                            "Depending on your business requirements (and budget), here are 10 WhatsApp Business API platforms that might do the job better for your business.",
                            "We’ve compared features, limitations, and pricing so you get a complete picture of what you’re signing up for.",
                        ]}
                    />

                    <OverviewGrid data={content} />

                    <p className="text-xl">Note: The prices listed in the article are the monthly cost if you choose the annual billing option.</p>

                    <div className="flex flex-col mt-10">
                        <section id="Gallabox">
                            <AlternativeInformation data={gallabox} />
                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">
                                        Moreover — unlike DoubleTick — Gallabox comes with plenty of
                                        <a href="https://gallabox.com/whatsapp-chatbot-templates" className="underline px-1">
                                            ready-to-use bot templates
                                        </a>
                                        and message templates so you don’t have to start from scratch. This means you can go beyond simple WhatsApp interactions and engage with
                                        customers at every touchpoint.
                                    </p>
                                }
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Gallabox’s best features",
                                    featuresList: [
                                        {
                                            item: "Gallabox GenAI: Leverage generative AI to draft personalized messages and responses, ensuring quick, context-aware communication",
                                        },
                                        {
                                            item: "No-code chatbots: Create and deploy AI-powered, multilingual chatbots to handle FAQs and provide a human-like support experience.",
                                        },
                                        {
                                            item: "Smart agent routing: Automatically assign incoming customer messages to the most qualified or available agent, ensuring faster resolutions and a better customer experience.",
                                        },
                                        {
                                            item: "Drip campaigns: Set up automated message sequences that trigger based on customer behavior, helping nurture leads, re-engage users, and drive conversions over time.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Gallabox’s limitations",
                                    featuresList: [
                                        {
                                            item: "New users may experience an initial learning curve.",
                                        },
                                    ],
                                }}
                            />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Gallabox’s pricing",
                                    featuresList: [
                                        {
                                            item: (
                                                <li className="text-lg text-left">
                                                    Gallabox offers flexible pricing plans for tailored to the goals of small and medium businesses. Check out the plans
                                                    <a href="https://gallabox.com/pricing" className="underline text-blue-500 px-1">
                                                        here.
                                                    </a>
                                                </li>
                                            ),
                                        },
                                    ],
                                }}
                            />

                            <ClientFeedback
                                descriptions={
                                    <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                        &quot;Gallabox provides innovative features that streamline our WhatsApp follow-ups, including sequencing, bots, and Click-to-WhatsApp
                                        (CTWA) capabilities — all of which have proven highly effective in our testing.&quot;
                                    </p>
                                }
                                authorName="– Avinash Babu, Assistant Digital Marketing Manager, Footprint Real Estate"
                                className="border-white-500 border-2 "
                            />
                        </section>

                        <section id="QuickReply" className="pt-10">
                            <AlternativeInformation data={quickReply} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "QuickReply’s best features",
                                    featuresList: [
                                        {
                                            item: "Broadcast retry: Schedule up to 3 automated retries for failed WhatsApp Broadcasts due to Meta's frequency capping or other issues.",
                                        },
                                        {
                                            item: "Custom chatbots: Build rule-based and AI-driven bots for different workflows like handling FAQs, addressing order inquiries, and more.",
                                        },
                                        {
                                            item: "API/Payload Based Personalization: Customize CTAs (and quick reply options) based on a user’s previous responses.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "QuickReply’s limitations",
                                    featuresList: [
                                        {
                                            item: "QuickReply doesn’t provide native iOS or Android apps.",
                                        },
                                        { item: "Users have reported QuickReply’s user interface (UI) to be difficult to navigate." },
                                    ],
                                }}
                            />
                            <AlternativeFeature
                                featuresData={{
                                    title: "QuickReply’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starter: $39 per month",
                                        },
                                        {
                                            item: "Standard: $99 per month",
                                        },
                                        {
                                            item: "Plus: $199 per month",
                                        },
                                        {
                                            item: "Enterprise: Custom pricing",
                                        },
                                        {
                                            item: "Agent add-on: $10 per agent, per month (all plans)",
                                        },
                                    ],
                                }}
                            />

                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">
                                        Read more:
                                        <a href="https://gallabox.com/alternatives/quickreply" className="underline px-1">
                                            10 alternatives to QuickReply
                                        </a>
                                    </p>
                                }
                            />
                        </section>

                        <section id="Wati" className="pt-10">
                            <AlternativeInformation data={wati} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Wati’s best features",
                                    featuresList: [
                                        {
                                            item: "AI Knowbot: Use natural language processing (NLP) to analyze customer messages and provide relevant answers from a predefined knowledge base.",
                                        },
                                        {
                                            item: "Chatbot timer: Set a 24-hour countdown for customer responses. If there’s no reply, the chat closes automatically.",
                                        },
                                        {
                                            item: "Keyword-based catalogs: Send product catalogs automatically when customers use specific keywords.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Wati’s limitations",
                                    featuresList: [
                                        {
                                            item: "Agents can't add notes to closed chats in the Team Inbox, making follow-ups difficult. ",
                                        },
                                        {
                                            item: "Users have to subscribe to the Pro plan to use automations (including drip campaigns), which is double the cost of the base plan.",
                                        },
                                    ],
                                }}
                            />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Wati’s pricing",
                                    featuresList: [
                                        {
                                            item: "Growth: $39 per month for 5 users",
                                        },
                                        {
                                            item: "Pro: $79 per month for 5 users",
                                        },
                                        {
                                            item: "Business: $199 per month for 5 users",
                                        },
                                    ],
                                }}
                            />

                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">
                                        Read more:
                                        <a href="https://gallabox.com/compare/wati" className="underline px-1">
                                            Wati vs. Gallabox
                                        </a>
                                    </p>
                                }
                            />
                        </section>

                        <section id="Brevo" className="pt-10">
                            <AlternativeInformation data={brevo} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Brevo’s best features",
                                    featuresList: [
                                        {
                                            item: "360° customer view: Track customer interactions across email, WhatsApp, SMS, and more to get a complete picture of their journey.",
                                        },
                                        {
                                            item: "Scoring library: Use built-in scoring models like RFM (Recency, Frequency, Monetary value) or LTV (Lifetime Value) to segment customers.",
                                        },
                                        {
                                            item: "Drag-and-drop automation: Easily create custom workflows with a visual builder, automating tasks like welcome sequences, abandoned cart reminders, and re-engagement campaigns without coding.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Brevo’s limitations",
                                    featuresList: [
                                        {
                                            item: "Costs can add up quickly if you subscribe to all of Brevo’s plans — sales, marketing, conversations, and customer data.",
                                        },
                                        {
                                            item: "Automations only come with basic filters, making it difficult to manage multiple workflows.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Brevo’s pricing",
                                    featuresList: [
                                        {
                                            item: "Free",
                                        },
                                        {
                                            item: "Pro: $15 per user, per month",
                                        },
                                    ],
                                }}
                            />

                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">
                                        This is only for the Conversations Platform that supports WhatsApp, Instagram, and Facebook Messenger. You can check their pricing page for
                                        other features and custom pricing.
                                    </p>
                                }
                            />
                        </section>

                        <section id="Interakt" className="pt-10">
                            <AlternativeInformation data={interakt} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Interakt’s best features",
                                    featuresList: [
                                        {
                                            item: "Back-in-stock alerts: Set up WhatsApp flows to let customers know when a product is back in stock.",
                                        },
                                        {
                                            item: "Notifications library: Get started easily with Interakt’s template list for common use cases like order updates, seasonal promotions, and more.",
                                        },
                                        {
                                            item: "Concierge AI: Use Interakt’s AI assistant to build customer journeys and get recommendations on which campaigns to send to different contact lists.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Interakt’s limitations",
                                    featuresList: [
                                        {
                                            item: "App integrations vary by subscription level. The Starter plan supports only Shopify, while Growth includes three integrations. For more integrations, you'll need the Advanced or Enterprise plan.",
                                        },
                                        {
                                            item: "Some Shopify store owners have found Interakt’s customer support to be slow and not always reliable.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Interakt’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starter: $17 per month",
                                        },
                                        {
                                            item: "Growth: $40 per month",
                                        },
                                        {
                                            item: "Advanced: $50 per month",
                                        },
                                        {
                                            item: "Enterprise: Custom pricing",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Superlemon" className="pt-10">
                            <AlternativeInformation data={superlemon} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Superlemon’s best features",
                                    featuresList: [
                                        {
                                            item: "Campaigns: Run segmented WhatsApp campaigns to send promotions and recover abandoned carts.",
                                        },
                                        {
                                            item: "Tags: Use tags to categorize chats based on specific criteria like customer type or ticket status.",
                                        },
                                        {
                                            item: "Opt-in widgets: Use one of the five opt-in widgets to collect lead WhatsApp numbers and build your contact list.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Superlemon’s limitations",
                                    featuresList: [
                                        {
                                            item: "Only supports basic, rule-based automated message flows.",
                                        },
                                        {
                                            item: "Shopify customers have mentioned that the support is slow. Some have also reported being charged during the trial period due to a bug in the product.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Superlemon’s pricing",
                                    featuresList: [
                                        {
                                            item: "Free",
                                        },
                                        {
                                            item: "Engage: $15 per month",
                                        },
                                        {
                                            item: "Engage Plus: $40 per month",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Zixflow" className="pt-10">
                            <AlternativeInformation data={zixflow} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Zixflow’s best features",
                                    featuresList: [
                                        {
                                            item: "IF/THEN automations: Automate repetitive tasks like updating customer details or sending follow-ups with conditional, trigger-action automations.",
                                        },
                                        {
                                            item: "Email validation: Check if an email address is valid and deliverable before sending campaigns.",
                                        },
                                        {
                                            item: "Smart cadences: Set up automated, multi-channel drip campaigns across WhatsApp, email, and SMS that stop if someone replies to a message in the sequence.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Zixflow’s limitations",
                                    featuresList: [
                                        {
                                            item: "Doesn’t provide two-way, conversational WhatsApp chatbots.",
                                        },
                                        {
                                            item: "Limited direct integrations, and while it does integrate with Zapier, it only provides actions — no triggers.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Zixflow’s pricing",
                                    featuresList: [
                                        {
                                            item: "Free",
                                        },
                                        {
                                            item: "Marketing Stack: $39 per month for 3 users",
                                        },
                                        {
                                            item: "Business Stack: $69 per month for 3 users",
                                        },
                                    ],
                                }}
                            />

                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">
                                        Read more:
                                        <a href="https://gallabox.com/alternatives/zixflow" className="underline px-1">
                                            10 alternatives to Zixflow
                                        </a>
                                    </p>
                                }
                            />
                        </section>

                        <section id="AiSensy" className="pt-10">
                            <AlternativeInformation data={aiSensy} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "AiSensy’s best features",
                                    featuresList: [
                                        {
                                            item: "WhatsApp retargeting: Reconnect with customers through personalized follow-up campaigns tailored to their past broadcast interactions — whether they read, replied, or clicked your CTA.",
                                        },
                                        {
                                            item: "WhatsApp Webviews: Let customers browse products, fill out forms, and more without leaving WhatsApp by embedding interactive web pages right inside the chat.",
                                        },
                                        {
                                            item: "Built-in CRM: Keep track of leads with tags and attributes, making it easier to personalize conversations and qualify potential buyers.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "AiSensy’s limitations",
                                    featuresList: [
                                        {
                                            item: "You’re limited to five chatbot flows per month.",
                                        },
                                        {
                                            item: "AiSensy doesn’t provide any mobile apps.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "AiSensy’s pricing",
                                    featuresList: [
                                        {
                                            item: "Free",
                                        },
                                        {
                                            item: "Basic: $18 per month",
                                        },
                                        {
                                            item: "Pro: $40 per month",
                                        },
                                        {
                                            item: "Enterprise: Custom pricing",
                                        },
                                        {
                                            item: "Chatbot Flows: $40 per month for up to 5 flows",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Respond.io" className="pt-10">
                            <AlternativeInformation data={respondIo} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Respond.io’s best features",
                                    featuresList: [
                                        {
                                            item: "Respond AI: Use the AI Agent to answer basic queries and assign chats to the appropriate agent based on conversation theme and pre-defined routing rules.",
                                        },
                                        {
                                            item: "Automated lead qualification: Use pre-set questions and contact forms — across all channels — to capture lead information and qualify them.",
                                        },
                                        {
                                            item: "Newsletters: Send broadcasts across multiple channels while managing opt-in workflows to stay compliant and avoid bans.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Respond.io’s limitations",
                                    featuresList: [
                                        {
                                            item: "Some users have reported Respond.io to be slow to load, especially when updating chats and opening dashboards.",
                                        },
                                        {
                                            item: "Inactive contacts must be removed manually, which can be a hassle. Not only does it take up time, but since pricing is based on the number of contacts, keeping inactive ones can drive up your subscription costs.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Respond.io’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starter: $79 per month for 1000 contacts",
                                        },
                                        {
                                            item: "Growth: $159 per month for 1000 contacts",
                                        },
                                        {
                                            item: "Advanced: $279 per month for 1000 contacts",
                                        },
                                        {
                                            item: "Enterprise: Custom pricing",
                                        },
                                    ],
                                }}
                            />
                        </section>

                        <section id="Zoko" className="pt-10">
                            <AlternativeInformation data={zoko} />
                            <AlternativeFeature
                                featuresData={{
                                    title: "Zoko’s best features",
                                    featuresList: [
                                        {
                                            item: "Flow builder: Create custom WhatsApp automation flows for everything from order confirmations to abandoned cart recovery without writing any code.",
                                        },
                                        {
                                            item: "Chat Screen: This is Zoko’s version of the team inbox where you can assign chats, add private notes, and more.",
                                        },
                                        {
                                            item: "Pop-up campaigns: Add pop-up forms to landing pages to collect the WhatsApp numbers of website visitors.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Zoko’s limitations",
                                    featuresList: [
                                        {
                                            item: "It lacks certain advanced features that you might find in other tools (like not being able to delete messages) or creating custom chatbots.",
                                        },
                                        {
                                            item: "The Shopify integration is an add-on that costs an extra $4.99 per month.",
                                        },
                                    ],
                                }}
                            />

                            <AlternativeFeature
                                featuresData={{
                                    title: "Zoko’s pricing",
                                    featuresList: [
                                        {
                                            item: "Starter: $39.99 per month (1 agent)",
                                        },
                                        {
                                            item: "Plus: $64.99 per month (5 agents)",
                                        },
                                        {
                                            item: "Elite: $114.99 per month (10 agents)",
                                        },
                                        {
                                            item: "Max: $404.99 per month (30 agents)",
                                        },
                                        { item: "Zoko Flows: $5.99 per month, per custom flow" },
                                    ],
                                }}
                            />
                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">On top of the platform subscription fee, Zoko also charges an additional “fair use” fee depending on your usage.</p>
                                }
                            />
                            <ContentParagraph
                                content={
                                    <p className="text-lg pt-2">
                                        Read more:
                                        <a href="https://gallabox.com/zoko-alternative" className="underline px-1">
                                            Zoko vs. Gallabox
                                        </a>
                                    </p>
                                }
                            />
                        </section>
                    </div>
                </div>

                <AlternativesTopBannerCTASection />
            </section>

            <div className="max-w-6xl mx-auto py-20 px-2">
                <h2 className="text-black text-4xl font-semibold pb-5">Why look for a DoubleTick alternative?</h2>
                <ContentParagraph
                    content={[
                        "DoubleTick is a great WhatsApp Business API solution for simple WhatsApp automations and customer management, but it’s not without its cons. Here’s a deep dive into some of its drawbacks and why you might do better with one of the DoubleTick alternatives we’ve explored in this article. ",
                    ]}
                />

                <ContentParagraph
                    title="Lacks feature depth"
                    content={[
                        "Whether it’s complex flows or custom integrations via Zapier and Pabbly Connect, DoubleTick doesn't offer the same level of granular control or options as some alternatives. For instance, the native Shopify integration is rather basic and doesn’t support advanced flows like product-specific abandoned cart or product browsing abandonment flows.",
                        "They also don’t provide other features like dead audience filtering (your broadcasts will go out to inactive audiences unless you clean your contact list manually) and revenue attribution for ads and campaigns.",
                        "Similarly, DoubleTick doesn’t give you any templates — you’ll need to build everything from scratch. And despite being a “sales CRM,” multiple reviews on G2 mention not enough CRM features to be a big minus with DoubleTick. It also doesn’t integrate out of the box with popular CRMs like Zoho CRM or Hubspot. You’ll need to use Zapier or webhooks (if you require advanced workflows).",
                    ]}
                />

                <ContentParagraph
                    title="A somewhat glitchy user experience"
                    content={
                        <>
                            <p className="text-lg pb-6 text-left">
                                Another drawback with DoubleTick is its user experience. For instance, if you have a Welcome bot and a keyword-trigger bot, the former takes
                                precedence even if customers say the “keyword.” And if you&apos;re using the DoubleTick order bot, you&apos;ll need to first integrate with another
                                of their products — QuickSell — to create your product catalogs beforehand.
                            </p>
                            <p className="text-lg pb-6 text-left">
                                You&apos;ll also need to use webhooks to trigger bot flows if your online store requires custom integrations.
                                <Image
                                    width={800}
                                    height={450}
                                    src="https://assets.gallabox.com/gb-home/alternatives/doubletick-io/user-experience.png"
                                    alt="plan-details"
                                    className="py-5"
                                />
                            </p>
                        </>
                    }
                />
                <ContentParagraph
                    title="Steep learning curve, limited support"
                    content={
                        <p className="text-lg pb-6 text-left">
                            DoubleTick isn’t the easiest platform to use — especially if you’re a non-technical user or someone not familiar with how WhatsApp Business API works.
                            At the same time, users have reported DoubleTick’s customer support to be quite slow, pointing users toward videos and webinars to get questions
                            answered, rather than one-on-one conversations.
                        </p>
                    }
                />
                <ContentParagraph
                    title="Can get expensive"
                    content={
                        <>
                            <p className="text-lg pb-6 text-left">
                                A common feedback amongst users on review platforms like G2 is DoubleTick&apos;s high subscription costs. Especially as they don&apos;t provide
                                monthly or even quarterly subscription plans — only yearly plans.
                            </p>
                            <p className="text-lg pb-6 text-left">
                                Moreover, features like AI automations, GPT-powered bots, and complex journeys are all available only in the Enterprise plan. And finally,
                                there&apos;s no free trial. So you have no way to gauge the platform&apos;s effectiveness before signing the deal.
                                <Image
                                    width={800}
                                    height={450}
                                    src="https://assets.gallabox.com/gb-home/alternatives/doubletick-io/feedback.png"
                                    alt="plan-details"
                                    className="py-5"
                                />
                            </p>
                        </>
                    }
                />

                <ClientFeedback
                    descriptions={
                        <>
                            <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                TL;DR: DoubleTick&apos;s WhatsApp automation is great for sales, but its rigid structure means you&apos;ll adapt to it — not the other way around.
                                Not to mention that it comes with a steep price tag.
                            </p>
                            <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                If you find yourself working around its limits instead of working toward your goals, it might be time to look for an alternative.
                            </p>
                        </>
                    }
                    className="border-white-500 border-2"
                />
                <h6 className="text-black text-xl font-semibold py-5">Run Customer-First WhatsApp Campaigns with Gallabox</h6>

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            Choosing the right WhatsApp marketing platform depends on your business needs — whether it&apos;s sales automation, AI-driven chatbots, or multi-channel
                            messaging. While tools like DoubleTick, AiSensy, and Zoko cater to different strengths,
                            <a href="https://gallabox.com/" className="underline px-1">
                                Gallabox
                            </a>
                            stands out for its balance of automation, ease of use, and customer-first approach. With no-code workflows, team collaboration tools, and
                            <a href="https://gallabox.com/whatsapp-integrations" className="underline px-1">
                                40+ direct integrations,
                            </a>
                            it helps businesses
                        </p>
                    }
                />

                <ContentParagraph
                    content={[
                        "across industries engage customers meaningfully, nurture leads effectively, and drive real conversions.",
                        "If you're looking for a WhatsApp automation tool that prioritizes both efficiency and personalization, we recommend giving Gallabox a try. And you don’t have to take our word for it. ",
                    ]}
                />

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            <a href="https://app.gallabox.com/signup" className="underline px-1">
                                Gallabox
                            </a>
                            and see for yourself how Gallabox can help you run WhatsApp campaigns that engage, convert, and retain customers.
                        </p>
                    }
                />
            </div>

            <AlternativesCTASection />

            <section className="bg-gray-100 md:py-20 py-10">
                <div
                    className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 md:ml-32 text-center md:text-6xl text-4xl font-semibold  md:text-left"
                    style={{ fontFamily: "Degular Display" }}
                >
                    Related Alternatives
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto gap-y-10">
                    {alternativesCardsDetails
                        .filter((data) => data.id !== "doubletick")
                        .map((data, idx) => (
                            <AlternativesCards key={idx} {...data} />
                        ))}
                </div>
            </section>

            <AwardsSection />

            <CTASection title="Turn connections into conversions" description="Take Gallabox for a 7-day ride" />
        </NewLayout>
    );
};

export default DoubleTickIo;
